import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Empty,
    Input,
    Pagination,
    Segmented,
    Select,
    Spin,
    Typography,
} from "antd";
import { InboxOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import { listInboxCategories, listInboxConversations, listInboxMailboxes } from "../../../api/v3/mailHubApi";
import ConversationListItem from "./components/ConversationListItem";
import MailReadingSurface from "./components/MailReadingSurface";
import {
    buildInboxConversationListViewModel,
    buildInboxMailboxOptions,
} from "./models/inboxConversationListViewModel";
import { formatContractError, requireArray, requireRecord } from "./models/contractValidation";
import { buildMailClassificationDisplay } from "./models/mailClassificationDisplay";
import "./InboxListPage.css";

const { Text, Title } = Typography;

const DEFAULT_PAGE_SIZE = 20;
const VIEW_CONVERSATIONS = "conversations";
const VIEW_RECEIPTS = "receipts";
const SPLIT_LAYOUT_MEDIA_QUERY = "(min-width: 1100px)";
const BJA_TRIGGER_STATUS_ALL = "all";
const CATEGORY_ALL = "all";
const BJA_TRIGGER_STATUS_OPTIONS = [
    { label: "全部", value: BJA_TRIGGER_STATUS_ALL },
    { label: "已触发 BJA", value: "triggered" },
    { label: "未触发 BJA", value: "not_triggered" },
];

function extract(response) {
    return response?.data?.data;
}

function extractErrorMessage(error, fallback) {
    return error?.response?.data?.error
        || error?.response?.data?.details
        || error?.message
        || fallback;
}

function isSplitLayoutViewport() {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return window.matchMedia(SPLIT_LAYOUT_MEDIA_QUERY).matches;
}

function buildConversationDetailPath(conversation) {
    if (conversation.threadId) {
        return `/mail-hub/threads/${encodeURIComponent(conversation.threadId)}`;
    }
    if (conversation.latestReceiptId) {
        return `/mail-hub/inbox/${encodeURIComponent(conversation.latestReceiptId)}`;
    }
    return "";
}

const InboxListPage = () => {
    const navigate = useNavigate();
    const [activeView, setActiveView] = useState(VIEW_CONVERSATIONS);
    const [loading, setLoading] = useState(false);
    const [mailboxLoading, setMailboxLoading] = useState(false);
    const [categoryLoading, setCategoryLoading] = useState(false);
    const [mailboxError, setMailboxError] = useState("");
    const [categoryError, setCategoryError] = useState("");
    const [conversationError, setConversationError] = useState("");
    const [mailboxes, setMailboxes] = useState([]);
    const [categories, setCategories] = useState([]);
    const [conversations, setConversations] = useState([]);
    const [selectedKey, setSelectedKey] = useState("");
    const [pagination, setPagination] = useState({ page: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0, totalPages: 0 });
    const [filters, setFilters] = useState({ mailboxId: undefined, query: "", category: undefined, bjaTriggerStatus: undefined, page: 1, pageSize: DEFAULT_PAGE_SIZE });
    const [searchText, setSearchText] = useState("");

    const mailboxOptions = useMemo(() => buildInboxMailboxOptions(mailboxes), [mailboxes]);
    const categoryOptions = useMemo(() => [
        { label: "全部分类", value: CATEGORY_ALL },
        ...categories.map((category) => ({
            label: buildMailClassificationDisplay({ category }).categoryLabel || category,
            value: category,
        })),
    ], [categories]);

    const selectedConversation = useMemo(
        () => conversations.find((item) => item.key === selectedKey) || null,
        [conversations, selectedKey],
    );

    const hasActiveFilters = Boolean(filters.mailboxId || filters.query || filters.category || filters.bjaTriggerStatus);

    const loadMailboxes = useCallback(async () => {
        setMailboxLoading(true);
        setMailboxError("");
        try {
            const response = await listInboxMailboxes();
            const payload = requireRecord(extract(response), "inboxMailboxes");
            setMailboxes(requireArray(payload.mailboxes, "inboxMailboxes.mailboxes"));
        } catch (error) {
            setMailboxError(formatContractError(error, extractErrorMessage(error, "加载可见邮箱失败")));
        } finally {
            setMailboxLoading(false);
        }
    }, []);

    const loadCategories = useCallback(async () => {
        setCategoryLoading(true);
        setCategoryError("");
        try {
            const response = await listInboxCategories();
            const payload = requireRecord(extract(response), "inboxCategories");
            setCategories(requireArray(payload.categories, "inboxCategories.categories"));
        } catch (error) {
            setCategories([]);
            setCategoryError(formatContractError(error, extractErrorMessage(error, "加载邮件分类失败")));
        } finally {
            setCategoryLoading(false);
        }
    }, []);

    const loadConversations = useCallback(async () => {
        setLoading(true);
        setConversationError("");
        try {
            const response = await listInboxConversations({
                mailbox_id: filters.mailboxId || undefined,
                query: filters.query.trim() || undefined,
                category: filters.category || undefined,
                bja_trigger_status: filters.bjaTriggerStatus || undefined,
                page: filters.page,
                page_size: filters.pageSize,
            });
            const viewModel = buildInboxConversationListViewModel(extract(response));
            setConversations(viewModel.items);
            setPagination(viewModel.pagination);
            setSelectedKey((current) => {
                if (viewModel.items.length === 0) return "";
                if (viewModel.items.some((item) => item.key === current)) return current;
                return "";
            });
        } catch (error) {
            setConversations([]);
            setPagination({ page: filters.page, pageSize: filters.pageSize, total: 0, totalPages: 0 });
            setSelectedKey("");
            setConversationError(formatContractError(error, extractErrorMessage(error, "加载收件箱会话失败")));
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        loadMailboxes();
    }, [loadMailboxes]);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    useEffect(() => {
        if (activeView === VIEW_CONVERSATIONS) {
            loadConversations();
        }
    }, [activeView, loadConversations]);

    const updateFilters = useCallback((patch) => {
        setFilters((current) => ({ ...current, ...patch, page: 1 }));
    }, []);

    const handleSearchChange = useCallback((event) => {
        const value = event.target.value;
        setSearchText(value);
        if (!value && filters.query) {
            updateFilters({ query: "" });
        }
    }, [filters.query, updateFilters]);

    const handleSearch = useCallback((value) => {
        updateFilters({ query: value.trim() });
    }, [updateFilters]);

    const handleBjaTriggerStatusChange = useCallback((value) => {
        updateFilters({ bjaTriggerStatus: value === BJA_TRIGGER_STATUS_ALL ? undefined : value });
    }, [updateFilters]);

    const handleCategoryChange = useCallback((value) => {
        updateFilters({ category: value === CATEGORY_ALL ? undefined : value });
    }, [updateFilters]);

    const handlePageChange = useCallback((page, pageSize) => {
        setFilters((current) => ({ ...current, page, pageSize }));
    }, []);

    const handleConversationSelect = useCallback((conversation) => {
        setSelectedKey(conversation.key);

        if (!isSplitLayoutViewport()) {
            const detailPath = buildConversationDetailPath(conversation);
            if (detailPath) {
                navigate(detailPath);
            }
        }
    }, [navigate]);

    const renderConversationList = () => {
        if (conversationError) {
            return (
                <div className="inbox-state-panel">
                    <Alert
                        type="error"
                        showIcon
                        message="收件箱加载失败"
                        description={conversationError}
                    />
                </div>
            );
        }

        if (loading && conversations.length === 0) {
            return (
                <div className="inbox-state-panel">
                    <Spin tip="正在加载会话" />
                </div>
            );
        }

        if (!loading && conversations.length === 0) {
            return (
                <div className="inbox-state-panel">
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={hasActiveFilters ? "暂无匹配的邮件会话" : "暂无可见邮件会话"}
                    />
                </div>
            );
        }

        return (
            <Spin spinning={loading}>
                <ul className="inbox-conversation-list" aria-label="收件箱会话列表">
                    {conversations.map((conversation) => (
                        <li key={conversation.key} className="inbox-conversation-list-row">
                            <ConversationListItem
                                conversation={conversation}
                                selected={conversation.key === selectedKey}
                                onSelect={() => handleConversationSelect(conversation)}
                            />
                        </li>
                    ))}
                </ul>
            </Spin>
        );
    };

    return (
        <section className="mail-hub-inbox-page">
            <header className="mail-hub-inbox-hero">
                <div className="mail-hub-inbox-title-block">
                    <div className="mail-hub-inbox-kicker">
                        <InboxOutlined />
                        <span>Mail Hub</span>
                    </div>
                    <Title level={2} className="mail-hub-inbox-title">收件箱</Title>
                    <Text className="mail-hub-inbox-subtitle">
                        按会话组织业务邮件，保留邮箱副本、附件和业务关联信号。
                    </Text>
                </div>
                <Button icon={<ReloadOutlined />} onClick={loadConversations} loading={loading} disabled={activeView !== VIEW_CONVERSATIONS}>
                    刷新
                </Button>
            </header>

            <div className="mail-hub-inbox-toolbar">
                <Segmented
                    value={activeView}
                    onChange={setActiveView}
                    options={[
                        { label: "会话视图", value: VIEW_CONVERSATIONS },
                        { label: "副本视图", value: VIEW_RECEIPTS },
                    ]}
                />
                <Select
                    allowClear
                    showSearch
                    loading={mailboxLoading}
                    className="mail-hub-inbox-mailbox-filter"
                    placeholder="筛选收件邮箱"
                    options={mailboxOptions}
                    value={filters.mailboxId}
                    optionFilterProp="label"
                    onChange={(value) => updateFilters({ mailboxId: value })}
                />
                <div className="mail-hub-inbox-filter-field mail-hub-inbox-category-filter">
                    <Text className="mail-hub-inbox-filter-label">邮件分类</Text>
                    <Select
                        aria-label="邮件分类"
                        loading={categoryLoading}
                        className="mail-hub-inbox-category-select"
                        options={categoryOptions}
                        value={filters.category || CATEGORY_ALL}
                        onChange={handleCategoryChange}
                    />
                </div>
                <div className="mail-hub-inbox-filter-field mail-hub-inbox-bja-filter">
                    <Text className="mail-hub-inbox-filter-label">BJA 触发状态</Text>
                    <Select
                        aria-label="BJA 触发状态"
                        className="mail-hub-inbox-bja-select"
                        options={BJA_TRIGGER_STATUS_OPTIONS}
                        value={filters.bjaTriggerStatus || BJA_TRIGGER_STATUS_ALL}
                        onChange={handleBjaTriggerStatusChange}
                    />
                </div>
                <Input.Search
                    allowClear
                    className="mail-hub-inbox-search"
                    enterButton={<SearchOutlined />}
                    placeholder="搜索主题或发件人"
                    value={searchText}
                    onChange={handleSearchChange}
                    onSearch={handleSearch}
                />
            </div>

            {mailboxError ? (
                <Alert
                    className="mail-hub-inbox-alert"
                    type="warning"
                    showIcon
                    message="邮箱列表加载失败"
                    description={mailboxError}
                />
            ) : null}

            {categoryError ? (
                <Alert
                    className="mail-hub-inbox-alert"
                    type="warning"
                    showIcon
                    message="邮件分类加载失败"
                    description={categoryError}
                />
            ) : null}

            {activeView === VIEW_RECEIPTS ? (
                <div className="mail-hub-inbox-panel">
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="副本视图正在开发中" />
                </div>
            ) : (
                <div className="mail-hub-inbox-split">
                    <div className="mail-hub-inbox-list-pane">
                        <div className="mail-hub-inbox-panel mail-hub-inbox-list-panel">
                            <div className="mail-hub-inbox-list-header">
                                <Text strong>业务邮件会话</Text>
                                <Text type="secondary">共 {pagination.total} 个会话</Text>
                            </div>
                            {renderConversationList()}
                            {pagination.total > 0 ? (
                                <div className="mail-hub-inbox-pagination">
                                    <Pagination
                                        current={pagination.page}
                                        pageSize={pagination.pageSize}
                                        total={pagination.total}
                                        showSizeChanger
                                        pageSizeOptions={["10", "20", "50"]}
                                        showTotal={(total) => `共 ${total} 个会话`}
                                        onChange={handlePageChange}
                                    />
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <aside className="mail-hub-inbox-reading-panel" aria-label="邮件阅读面">
                        {selectedConversation ? (
                            <MailReadingSurface
                                entry="inbox"
                                threadId={selectedConversation.threadId}
                                receiptId={selectedConversation.latestReceiptId}
                            />
                        ) : (
                            <div className="mail-hub-inbox-reading-placeholder">
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description="选择一封邮件查看详情"
                                />
                            </div>
                        )}
                    </aside>
                </div>
            )}
        </section>
    );
};

export default InboxListPage;
